import type { Instant, LocalDate } from "adl-gen/common";
import type {
  NewDealRequestState,
  NewDealRequestType,
} from "adl-gen/ferovinum/app/db";
import {
  type ExistingStockProcurementStateCode,
  type NewStockProcurementStateCode,
  valuesExistingStockProcurementStateCode,
  valuesNewStockProcurementStateCode,
} from "adl-gen/ferovinum/app/procurement";
import {
  mapExistingStockProcurementStateCode,
  mapNewStockProcurementStateCode,
} from "utils/conversion-utils";
import { toLondonZonedLocalDate } from "utils/date-utils";
import { assertNever } from "utils/hx/util/types";
import {
  BaseProgressStepper,
  compactStepperStates,
} from "../../base-progress-stepper/base-progress-stepper";

type ProcurementStateCode =
  | NewStockProcurementStateCode
  | ExistingStockProcurementStateCode;

// Gets the progress stepper label for in progress new deal requests
function getProgressStepperLabelFromState(state: ProcurementStateCode) {
  switch (state) {
    case "NEWDEAL":
      return "Purchase order requested";
    case "FEROACCEPTED":
      return "Ferovinum accepted";
    case "ORGACCEPTED":
      return "Contract note accepted";
    case "DOGENERATED":
      return "Delivery order sent to organisation";
    case "DOSIGNED":
      return "Delivery order signed";
    case "DEPOSITINVOICED":
      return "Deposit invoice sent";
    case "DEPOSITPAID":
      return "Deposit paid";
    case "DEPOSITPAYMENTRECEIVED":
      return "Deposit received";
    case "POCREATED":
      return "Purchase order sent to supplier";
    case "POACCEPTED":
      return "Purchase order accepted by supplier";
    case "COLLECTIONREQUESTED":
      return "Availability date provided";
    case "COLLECTIONACCEPTED":
      return "Availability date confirmed";
    case "COLLECTIONCONFIRMED":
      return "Collected";
    case "CARRIERDELIVERYCONFIRMED":
      return "Delivered";
    case "WAREHOUSEDELIVERYCONFIRMED":
      return "Landed";
    case "STOCKTRANSFERRED":
      return "Transfer confirmed by storage location";
    case "PAYMENTTRANSFERRED":
      return "Ferovinum paid";
    case "FEROVOIDED":
      return "Contract note voided.";
    case "CANCELLED":
      return "Cancelled";
    case "FEROREJECTED":
      return "Contract note rejected";
    case "ORGREJECTED":
      return "Organisation rejected";
    default:
      assertNever(state);
  }
}

function isProgressStateCode(stateCode: ProcurementStateCode) {
  switch (stateCode) {
    case "NEWDEAL":
    case "FEROACCEPTED":
    case "ORGACCEPTED":
    case "DOGENERATED":
    case "DOSIGNED":
    case "DEPOSITINVOICED":
    case "DEPOSITPAID":
    case "DEPOSITPAYMENTRECEIVED":
    case "POCREATED":
    case "POACCEPTED":
    case "COLLECTIONREQUESTED":
    case "COLLECTIONACCEPTED":
    case "COLLECTIONCONFIRMED":
    case "CARRIERDELIVERYCONFIRMED":
    case "WAREHOUSEDELIVERYCONFIRMED":
    case "STOCKTRANSFERRED":
    case "PAYMENTTRANSFERRED":
      return true;
    case "FEROREJECTED":
    case "ORGREJECTED":
    case "CANCELLED":
    case "FEROVOIDED":
      return false;
    default:
      assertNever(stateCode);
  }
}

export interface NewDealRequestProgressStepperProps {
  variant: NewDealRequestType;
  currentState: NewDealRequestState;
  lastStateSet: Instant;
  upcomingStateDeadline?: LocalDate;
  actionRequiredStateCode: ProcurementStateCode;
  compact?: boolean;
}

export const NewDealRequestProgressStepper = ({
  variant,
  currentState,
  lastStateSet,
  upcomingStateDeadline,
  actionRequiredStateCode,
  compact = false,
}: NewDealRequestProgressStepperProps) => {
  const { stateCodes, currentStateCode } = getNewDealRequestStateCodes(
    variant,
    currentState,
  );

  const displayStateCodes = !isProgressStateCode(currentStateCode)
    ? [currentStateCode]
    : compact
      ? compactStepperStates(stateCodes, currentStateCode)
      : stateCodes;
  const displayStates = displayStateCodes.map((s) => ({
    code: s,
    label: getProgressStepperLabelFromState(s),
  }));
  const stateSetAt = toLondonZonedLocalDate(lastStateSet);

  return (
    <BaseProgressStepper<ProcurementStateCode>
      currentState={{
        code: currentStateCode,
        setAt: stateSetAt,
        deadline: upcomingStateDeadline ?? null,
      }}
      displayStates={displayStates}
      actionRequiredState={actionRequiredStateCode}
    />
  );
};

function getNewDealRequestStateCodes(
  variant: NewDealRequestType,
  currentState: NewDealRequestState,
): {
  stateCodes: ProcurementStateCode[];
  currentStateCode: ProcurementStateCode;
} {
  const { stateCodes, currentStateCode } =
    variant === "newStock"
      ? getNewStockStates(currentState)
      : getExistingStockStates(currentState);
  return { stateCodes, currentStateCode };
}

function getNewStockStates(currentState: NewDealRequestState) {
  const currentStateCode = mapNewStockProcurementStateCode(currentState);
  const stateCodes =
    valuesNewStockProcurementStateCode.filter(isProgressStateCode);
  return { stateCodes, currentStateCode };
}

function getExistingStockStates(currentState: NewDealRequestState) {
  const currentStateCode = mapExistingStockProcurementStateCode(currentState);
  const stateCodes = (
    isDOstate(currentStateCode)
      ? valuesExistingStockProcurementStateCode
      : valuesExistingStockProcurementStateCode.filter((s) => !isDOstate(s))
  ).filter(isProgressStateCode);
  return { stateCodes, currentStateCode };
}

function isDOstate(state: ExistingStockProcurementStateCode) {
  return state === "DOGENERATED" || state === "DOSIGNED";
}
